import React, { useState, useEffect, useCallback } from 'react';
import { FaPlus, FaKey, FaCopy, FaCheck, FaTrash, FaExclamationTriangle } from 'react-icons/fa';
import api from '../../../services/api';
import { formatAppDateTime } from '../../../utils/dateUtils';

// --- Confirmation Dialog (mirrors UserManagement) ---
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmLabel,
  confirmClass,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
        <p className="text-sm text-slate-600 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${confirmClass || 'bg-red-500 hover:bg-red-600'}`}
          >
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Generate Key Modal (name entry) ---
const GenerateKeyModal = ({ isOpen, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setName('');
      setFormError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError('Please enter a name for this key.');
      return;
    }
    try {
      setSaving(true);
      await onSave(name.trim());
    } catch (err) {
      setFormError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          'Failed to generate key.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Generate API Key</h2>
          <p className="text-sm text-slate-600 mt-1">
            Give this key a name so you can recognise it later.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Key Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g. laptop, recon-workbook"
              maxLength={100}
              autoFocus
              required
            />
            <p className="mt-1 text-xs text-slate-500">
              A short label like &ldquo;laptop&rdquo; or &ldquo;recon-workbook&rdquo;.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Generating...
                </>
              ) : (
                <>
                  <FaKey className="text-xs" />
                  Generate Key
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Result Modal (raw key shown once) ---
const KeyResultModal = ({ isOpen, result, onClose }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) setCopied(false);
  }, [isOpen]);

  if (!isOpen || !result) return null;

  const rawKey = result.raw_key || '';

  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(rawKey);
      } else {
        // Fallback for non-secure contexts / older browsers
        const textarea = document.createElement('textarea');
        textarea.value = rawKey;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Leave copied=false; the key is visible for manual copy.
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-surface rounded-xl shadow-xl max-w-lg w-full mx-4">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">API Key Created</h2>
          <p className="text-sm text-slate-600 mt-1">
            {result.key_name ? (
              <>
                Key <span className="font-medium">&ldquo;{result.key_name}&rdquo;</span> is ready.
              </>
            ) : (
              'Your new key is ready.'
            )}
          </p>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
            <FaExclamationTriangle className="flex-shrink-0 mt-0.5" />
            <span>
              This key is shown only once &mdash; store it securely. You will not be able to see it
              again.
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Your API Key</label>
            <div className="flex items-stretch gap-2">
              <code className="flex-1 px-3 py-2 bg-slate-900 dark:bg-slate-50 text-green-300 dark:text-green-800 rounded-lg text-xs font-mono break-all select-all">
                {rawKey}
              </code>
              <button
                type="button"
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
                title="Copy key to clipboard"
              >
                {copied ? (
                  <>
                    <FaCheck className="text-xs" /> Copied
                  </>
                ) : (
                  <>
                    <FaCopy className="text-xs" /> Copy
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="flex justify-end pt-1">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---
const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // null = unknown (assume enabled UI), true = enabled, false = disabled
  const [accessEnabled, setAccessEnabled] = useState(null);

  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [keyResult, setKeyResult] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: '',
    confirmClass: '',
    onConfirm: () => {},
  });

  const fetchKeys = useCallback(async () => {
    try {
      setError(null);
      const response = await api.odataKeys.list();
      const data = response.data;
      // Accept both { keys: [...] } and a bare array.
      const list = Array.isArray(data) ? data : data?.keys || [];
      setKeys(list);
      // If the backend surfaces the access flag in the list response, honour it.
      const flag = Array.isArray(data)
        ? undefined
        : (data?.api_access_enabled ?? data?.apiAccessEnabled);
      if (typeof flag === 'boolean') {
        setAccessEnabled(flag);
      }
    } catch (err) {
      console.error('Error fetching API keys:', err);
      if (err?.response?.status === 403) {
        setAccessEnabled(false);
      } else {
        setError('Failed to load API keys. Please try again.');
      }
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchKeys();
      setLoading(false);
    };
    load();
  }, [fetchKeys]);

  const handleGenerate = async (name) => {
    try {
      const response = await api.odataKeys.create(name);
      setGenerateModalOpen(false);
      setKeyResult(response.data);
      setAccessEnabled(true);
      await fetchKeys();
    } catch (err) {
      // A 403 here means access was disabled after the page loaded.
      if (err?.response?.status === 403) {
        setAccessEnabled(false);
        setGenerateModalOpen(false);
      }
      // Re-throw so the modal can display validation / duplicate / limit errors.
      throw err;
    }
  };

  const handleRevoke = (key) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Revoke API Key',
      message: `Revoke "${key.key_name}"? Any Excel or Power BI refresh using this key will stop working within a minute. This cannot be undone.`,
      confirmLabel: 'Revoke Key',
      confirmClass: 'bg-red-500 hover:bg-red-600',
      onConfirm: async () => {
        try {
          await api.odataKeys.revoke(key.key_id);
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          await fetchKeys();
        } catch (err) {
          console.error('Error revoking API key:', err);
          setError('Failed to revoke API key.');
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const activeCount = keys.filter((k) => k.is_active).length;
  const showDisabled = accessEnabled === false;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-surface border-b border-slate-200 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">API Keys</h1>
              <p className="text-slate-600 text-sm mt-1">
                Personal keys for refreshing the OData feed in Excel or Power BI
              </p>
            </div>
            {!showDisabled && (
              <button
                onClick={() => setGenerateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
              >
                <FaPlus className="text-sm" />
                Generate API Key
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-start">
            <svg
              className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 ml-4 text-red-400 hover:text-red-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-primary-500"></div>
            <p className="mt-4 text-slate-600">Loading API keys...</p>
          </div>
        ) : showDisabled ? (
          /* Disabled state */
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
            <div className="text-center py-14 px-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <FaKey className="w-7 h-7 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">API access is disabled</h3>
              <p className="text-slate-600 max-w-md mx-auto">
                API access is disabled for your account. Contact an administrator to enable it.
              </p>
            </div>
          </div>
        ) : (
          /* Enabled state */
          <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Your Keys</h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  {activeCount} active key{activeCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              {keys.length === 0 ? (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                    <FaKey className="w-7 h-7 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No API Keys Yet</h3>
                  <p className="text-slate-600 mb-6 max-w-md mx-auto">
                    Generate a key to refresh the OData feed in Excel or Power BI. Your key works
                    together with your LexiBridge login when the feed refreshes.
                  </p>
                  <button
                    onClick={() => setGenerateModalOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
                  >
                    <FaPlus className="text-sm" />
                    Generate Your First Key
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full divide-y divide-slate-200">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Key
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider hidden sm:table-cell">
                          Created
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider hidden md:table-cell">
                          Last Used
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider w-12">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-surface divide-y divide-slate-200">
                      {keys.map((key) => (
                        <tr key={key.key_id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-slate-900">{key.key_name}</div>
                          </td>
                          <td className="px-4 py-3">
                            <code className="text-xs font-mono text-slate-600">
                              {key.key_prefix ? `${key.key_prefix}…` : '—'}
                            </code>
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <div className="text-sm text-slate-600">
                              {key.created_at ? formatAppDateTime(key.created_at) : '—'}
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="text-sm text-slate-600">
                              {key.last_used_at ? formatAppDateTime(key.last_used_at) : 'Never'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                key.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {key.is_active ? 'Active' : 'Revoked'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {key.is_active ? (
                              <button
                                onClick={() => handleRevoke(key)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                                title="Revoke key"
                              >
                                <FaTrash className="text-xs" />
                                Revoke
                              </button>
                            ) : (
                              <span className="text-xs text-slate-400">
                                {key.revoked_at
                                  ? `Revoked ${formatAppDateTime(key.revoked_at)}`
                                  : '—'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <GenerateKeyModal
        isOpen={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        onSave={handleGenerate}
      />

      <KeyResultModal isOpen={!!keyResult} result={keyResult} onClose={() => setKeyResult(null)} />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        confirmClass={confirmDialog.confirmClass}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default ApiKeys;
